import { useEffect, useState, useTransition } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, Textarea, Select, Label } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { LocationTypeahead } from "@/components/jobs/location-typeahead";
import type { JobVisibility, WizardAnswers } from "@/lib/types";
import { canonicalLocation, isKnownLocation } from "@/lib/locations";
import { useDict } from "@/lib/i18n/context";
import { resolveApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";

type ClientOption = { id: string; client_company_name: string };

function todayDateInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function JobWizard() {
  const t = useDict();
  const STEPS = t.jobs.wizardSteps;
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [partnershipId, setPartnershipId] = useState("");
  const [expiresAt, setExpiresAt] = useState(todayDateInput);
  const [visibility, setVisibility] = useState<JobVisibility>("private");
  const [a, setA] = useState<WizardAnswers>({
    title: "",
    seniority: "Mid",
    question_difficulty: "medium",
    employment_type: "Full-time",
    location: "",
    responsibilities: "",
    must_have: "",
    nice_to_have: "",
    salary_range: "",
    extra: "",
  });

  useEffect(() => {
    fetch("/api/partnerships", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json() as Promise<{ activeAsAgency?: ClientOption[] }>;
      })
      .then((data) => {
        if (data?.activeAsAgency) setClients(data.activeAsAgency);
      })
      .catch(() => {});
  }, []);

  const set = (k: keyof WizardAnswers) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => setA((p) => ({ ...p, [k]: e.target.value }));

  const listingOk = Boolean(expiresAt && expiresAt >= todayDateInput());
  const locationOk = isKnownLocation(a.location);
  const canNext =
    step === 0
      ? Boolean(a.title?.trim()) && locationOk
      : step === STEPS.length - 1
        ? listingOk
        : true;
  const isLast = step === STEPS.length - 1;

  function submit() {
    if (!listingOk) {
      setError(t.jobs.listingEndDateHint);
      return;
    }
    if (!isKnownLocation(a.location)) {
      const hybridOnly = /^hybrid$/i.test((a.location || "").trim());
      setError(
        hybridOnly ? t.jobs.locationHybridHint : t.jobs.locationPickHint,
      );
      return;
    }
    setError(null);
    startTransition(async () => {
      try {
        const location = canonicalLocation(a.location || "") || a.location;
        const res = await fetch("/api/jobs/generate", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...a,
            location,
            partnership_id: partnershipId || undefined,
            expires_at: expiresAt,
            visibility,
          }),
        });
        const data = (await res.json()) as {
          id?: string;
          error?: string;
          code?: string;
        };
        if (!res.ok || data.error)
          setError(resolveApiError(t, data, "jobCreateFailed"));
        else if (data.id) navigate(`/jobs/${data.id}`);
      } catch {
        setError(t.errors.networkError);
      }
    });
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                i <= step ? "bg-brand-600 text-white" : "bg-surface-hover text-muted"
              }`}
            >
              {i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`h-1 flex-1 rounded ${i < step ? "bg-brand-600" : "bg-surface-hover"}`}
              />
            )}
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <h2 className="text-xl font-bold">{STEPS[step]}</h2>

          {clients.length > 0 && step === 0 && (
            <div>
              <Label>{t.partners.workingFor}</Label>
              <Select
                value={partnershipId}
                onChange={(e) => setPartnershipId(e.target.value)}
              >
                <option value="">{t.partners.ownCompany}</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.client_company_name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label>{t.jobs.jobTitle} *</Label>
                <Input
                  value={a.title}
                  onChange={set("title")}
                  placeholder={t.jobs.jobTitlePh}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>{t.jobs.seniority}</Label>
                  <Select value={a.seniority} onChange={set("seniority")}>
                    {["Intern", "Junior", "Mid", "Senior", "Lead", "Manager"].map(
                      (o) => (
                        <option key={o}>{o}</option>
                      ),
                    )}
                  </Select>
                </div>
                <div>
                  <Label>{t.jobs.questionDifficulty}</Label>
                  <Select
                    value={a.question_difficulty || "medium"}
                    onChange={set("question_difficulty")}
                  >
                    <option value="simple">{t.jobs.difficultyEasy}</option>
                    <option value="medium">{t.jobs.difficultyMedium}</option>
                    <option value="advanced">{t.jobs.difficultyHard}</option>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>{t.jobs.employmentType}</Label>
                  <Select
                    value={a.employment_type}
                    onChange={set("employment_type")}
                  >
                    {["Full-time", "Part-time", "Contract", "Internship"].map(
                      (o) => (
                        <option key={o}>{o}</option>
                      ),
                    )}
                  </Select>
                </div>
                <div>
                  <Label>{t.jobs.location}</Label>
                  <LocationTypeahead
                    value={a.location || ""}
                    onChange={(location) => setA((p) => ({ ...p, location }))}
                    placeholder={t.jobs.locationPh}
                    noMatchesLabel={t.jobs.locationNoMatches}
                    hybridCityLabel={t.jobs.locationHybridCity}
                    hybridCityPh={t.jobs.locationHybridCityPh}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <Label>{t.jobs.responsibilities}</Label>
              <Textarea
                className="min-h-40"
                value={a.responsibilities}
                onChange={set("responsibilities")}
                placeholder={t.jobs.responsibilitiesPh}
              />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label>{t.jobs.mustHave}</Label>
                <Textarea
                  value={a.must_have}
                  onChange={set("must_have")}
                  placeholder={t.jobs.mustHavePh}
                />
              </div>
              <div>
                <Label>{t.jobs.niceToHave}</Label>
                <Textarea
                  value={a.nice_to_have}
                  onChange={set("nice_to_have")}
                  placeholder={t.jobs.niceToHavePh}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label>{t.jobs.salaryRange}</Label>
                <Input
                  value={a.salary_range}
                  onChange={set("salary_range")}
                  placeholder={t.jobs.salaryRangePh}
                />
              </div>
              <div>
                <Label>{t.jobs.anythingElse}</Label>
                <Textarea
                  value={a.extra}
                  onChange={set("extra")}
                  placeholder={t.jobs.anythingElsePh}
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div>
                <Label>{t.jobs.listingEndDate} *</Label>
                <Input
                  type="date"
                  value={expiresAt}
                  min={todayDateInput()}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  required
                />
                <p className="mt-1 text-xs text-muted">{t.jobs.listingEndDateHint}</p>
              </div>
              <div>
                <Label>{t.jobs.listingVisibility}</Label>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setVisibility("private")}
                    className={cn(
                      "rounded-xl border px-3 py-3 text-start text-sm transition",
                      visibility === "private"
                        ? "border-transparent bg-brand-600 text-white"
                        : "border-border text-foreground hover:bg-surface",
                    )}
                  >
                    {t.jobs.visibilityPrivate}
                  </button>
                  <button
                    type="button"
                    onClick={() => setVisibility("public")}
                    className={cn(
                      "rounded-xl border px-3 py-3 text-start text-sm transition",
                      visibility === "public"
                        ? "border-transparent bg-brand-600 text-white"
                        : "border-border text-foreground hover:bg-surface",
                    )}
                  >
                    {t.jobs.visibilityPublic}
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="flex justify-between pt-2">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0 || pending}
            >
              <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t.common.back}
            </Button>
            {isLast ? (
              <Button onClick={submit} loading={pending} disabled={!listingOk}>
                <Wand2 className="h-4 w-4" /> {t.jobs.generate}
              </Button>
            ) : (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext}
              >
                {t.common.next} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
